import { z } from 'zod';

/**
 * Operational Control wire types.
 *
 * Two conventions carried over from the financial half:
 *
 *   * money and quantities arrive as **decimal strings**, so no precision is
 *     lost on the way through JSON. Nothing here parses one into a number for
 *     arithmetic — the server has already done every calculation;
 *   * totals are records keyed by currency code. There is no FX service in
 *     this system, so a single «итого» across currencies would be invented.
 */
export const decimalString = z.string();

/** A record of currency code → decimal string. `{}` means "nothing owed". */
export const moneyByCurrencySchema = z.record(decimalString).default({});

// --- module access -----------------------------------------------------------

export const OPERATIONAL_MODULES = ['INVENTORY', 'SUPPLIERS_AP', 'DOCUMENTS_OCR'] as const;
export type OperationalModule = (typeof OPERATIONAL_MODULES)[number];

export const ACCESS_LEVELS = ['NONE', 'VIEW', 'UPLOAD', 'MANAGE'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export const accessLevelSchema = z.enum(ACCESS_LEVELS);
export const moduleSchema = z.enum(OPERATIONAL_MODULES);

export const MODULE_TITLES: Record<OperationalModule, string> = {
  INVENTORY: 'Инвентаризация',
  SUPPLIERS_AP: 'Поставщики и долги',
  DOCUMENTS_OCR: 'Документы / OCR',
};

export const ACCESS_TITLES: Record<AccessLevel, string> = {
  NONE: 'Нет',
  VIEW: 'Просмотр',
  UPLOAD: 'Загрузка',
  MANAGE: 'Управление',
};

/** The ordering the server uses. Kept here only to compare, never to decide. */
const RANK: Record<AccessLevel, number> = { NONE: 0, VIEW: 1, UPLOAD: 2, MANAGE: 3 };

/**
 * Whether a level satisfies a requirement.
 *
 * Used to hide controls the server would refuse. It is presentation following
 * authorization — every endpoint re-checks, and hiding a button has never been
 * what protects anything.
 */
export function atLeast(level: AccessLevel | undefined, required: AccessLevel): boolean {
  return RANK[level ?? 'NONE'] >= RANK[required];
}

export const moduleGrantSchema = z.object({
  module: moduleSchema,
  access: accessLevelSchema,
  title: z.string(),
  /** Levels this module offers — the UI renders exactly these radio buttons. */
  available: z.array(accessLevelSchema),
  updated_at: z.string().nullable().optional(),
  granted_by_id: z.string().nullable().optional(),
});

export const userModuleGrantsSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  full_name: z.string(),
  role_code: z.string(),
  /** True for the owner: the controls are shown fixed at «Управление». */
  is_owner: z.boolean(),
  grants: z.array(moduleGrantSchema),
});

export const operationalAccessSchema = z.object({
  modules: z.record(accessLevelSchema),
  is_owner: z.boolean(),
  has_any: z.boolean(),
});

// --- inventory ---------------------------------------------------------------

export const INVENTORY_STATUSES = ['DRAFT', 'VALIDATED', 'NEEDS_REVIEW', 'CONFIRMED'] as const;
export type InventoryStatus = (typeof INVENTORY_STATUSES)[number];

export const INVENTORY_STATUS_TITLES: Record<InventoryStatus, string> = {
  DRAFT: 'Черновик',
  VALIDATED: 'Проверено',
  NEEDS_REVIEW: 'Нужна проверка',
  CONFIRMED: 'Подтверждено',
};

export const referenceSchema = z.object({
  zones: z.array(z.object({ code: z.string(), title: z.string() })),
  units: z.array(z.object({ code: z.string(), title: z.string() })),
  statuses: z.array(z.string()),
});

export const inventoryIssueSchema = z.object({
  severity: z.enum(['ERROR', 'WARNING']),
  code: z.string(),
  message: z.string(),
  row_number: z.number().nullable().optional(),
  name: z.string().default(''),
});

export const inventoryLineSchema = z.object({
  id: z.string(),
  row_number: z.number(),
  catalog_item_id: z.string().nullable(),
  name: z.string(),
  raw_name: z.string(),
  quantity: decimalString,
  /** The machine value (KG, PIECE…). */
  unit: z.string(),
  /** The same unit as a person writes it («кг»). Rendered; never derived here. */
  unit_title: z.string(),
  unit_price: decimalString.nullable(),
  total_value: decimalString.nullable(),
  currency: z.string(),
  matched: z.boolean(),
});

export const inventoryDocumentSchema = z.object({
  id: z.string(),
  business_unit_id: z.string(),
  business_unit_name: z.string(),
  zone: z.string(),
  zone_title: z.string(),
  inventory_date: z.string(),
  status: z.enum(INVENTORY_STATUSES),
  source_type: z.string(),
  line_count: z.number(),
  totals_by_currency: moneyByCurrencySchema,
  error_count: z.number(),
  warning_count: z.number(),
  created_at: z.string(),
  created_by_id: z.string().nullable().optional(),
  confirmed_at: z.string().nullable().optional(),
  confirmed_by_id: z.string().nullable().optional(),
  corrects_document_id: z.string().nullable().optional(),
  superseded_at: z.string().nullable().optional(),
  source_filename: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
});

export const inventoryDocumentDetailSchema = inventoryDocumentSchema.extend({
  lines: z.array(inventoryLineSchema).default([]),
  issues: z.array(inventoryIssueSchema).default([]),
  new_items: z.array(z.string()).default([]),
  duplicate_items: z.array(z.string()).default([]),
  matched_items: z.array(z.string()).default([]),
  /** False whenever a blocking error stands. The button follows this. */
  can_confirm: z.boolean().default(false),
});

export const inventorySummarySchema = z.object({
  document_id: z.string(),
  business_unit_id: z.string(),
  business_unit_name: z.string(),
  zone: z.string(),
  inventory_date: z.string(),
  status: z.string(),
  line_count: z.number(),
  priced_line_count: z.number(),
  totals_by_currency: moneyByCurrencySchema,
  warning_count: z.number(),
  error_count: z.number(),
});

export const comparisonRowSchema = z.object({
  catalog_item_id: z.string().nullable(),
  name: z.string(),
  unit: z.string(),
  unit_title: z.string(),
  previous_unit: z.string().nullable(),
  previous_unit_title: z.string().nullable(),
  current_quantity: decimalString.nullable(),
  previous_quantity: decimalString.nullable(),
  delta_quantity: decimalString.nullable(),
  current_value: decimalString.nullable(),
  previous_value: decimalString.nullable(),
  delta_value: decimalString.nullable(),
  currency: z.string(),
  is_new: z.boolean(),
  is_missing: z.boolean(),
  /** Units differ between the two counts, so no delta is offered at all. */
  unit_changed: z.boolean(),
});

export const inventoryComparisonSchema = z.object({
  current: inventorySummarySchema,
  previous: inventorySummarySchema.nullable(),
  rows: z.array(comparisonRowSchema).default([]),
  new_item_count: z.number().default(0),
  missing_item_count: z.number().default(0),
  delta_by_currency: moneyByCurrencySchema,
  incomparable_count: z.number().default(0),
});

export const catalogItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  sku: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  unit: z.string(),
  active: z.boolean(),
  created_at: z.string(),
});

// --- suppliers & accounts payable --------------------------------------------

export const supplierSchema = z.object({
  id: z.string(),
  name: z.string(),
  legal_name: z.string().nullable().optional(),
  tax_id: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  active: z.boolean(),
  created_at: z.string(),
});

export const PAYABLE_STATUSES = ['UNPAID', 'PARTIALLY_PAID', 'PAID', 'OVERDUE'] as const;
export type PayableStatus = (typeof PAYABLE_STATUSES)[number];

export const PAYABLE_STATUS_TITLES: Record<PayableStatus, string> = {
  UNPAID: 'Не оплачен',
  PARTIALLY_PAID: 'Частично оплачен',
  PAID: 'Оплачен',
  OVERDUE: 'Просрочен',
};

export const payableSchema = z.object({
  id: z.string(),
  supplier_id: z.string(),
  supplier_name: z.string(),
  business_unit_id: z.string(),
  business_unit_name: z.string(),
  document_number: z.string().nullable(),
  document_date: z.string(),
  due_date: z.string().nullable(),
  amount: decimalString,
  /** Derived from the allocations on every read — never a stored balance. */
  paid_amount: decimalString,
  outstanding_amount: decimalString,
  currency: z.string(),
  status: z.enum(PAYABLE_STATUSES),
  is_overdue: z.boolean(),
  days_overdue: z.number().nullable(),
  description: z.string().nullable(),
  created_at: z.string(),
});

export const allocationSchema = z.object({
  id: z.string(),
  payment_id: z.string(),
  invoice_id: z.string(),
  invoice_number: z.string().nullable(),
  invoice_date: z.string(),
  amount: decimalString,
  created_at: z.string(),
});

export const paymentSchema = z.object({
  id: z.string(),
  supplier_id: z.string(),
  supplier_name: z.string(),
  business_unit_id: z.string(),
  business_unit_name: z.string(),
  payment_date: z.string(),
  amount: decimalString,
  currency: z.string(),
  reference: z.string().nullable(),
  /** Paid but not applied to any invoice — shown rather than hidden. */
  unallocated_amount: decimalString,
  allocations: z.array(allocationSchema).default([]),
  created_at: z.string(),
});

export const supplierBalanceSchema = z.object({
  supplier_id: z.string(),
  supplier_name: z.string(),
  outstanding_by_currency: moneyByCurrencySchema,
  overdue_by_currency: moneyByCurrencySchema,
  due_soon_by_currency: moneyByCurrencySchema,
  earliest_due_date: z.string().nullable(),
  open_invoice_count: z.number(),
  business_unit_names: z.array(z.string()).default([]),
});

export const apSummarySchema = z.object({
  outstanding_by_currency: moneyByCurrencySchema,
  overdue_by_currency: moneyByCurrencySchema,
  due_soon_by_currency: moneyByCurrencySchema,
  unallocated_by_currency: moneyByCurrencySchema,
  suppliers_with_debt: z.number(),
  overdue_invoice_count: z.number(),
  due_soon_days: z.number(),
});

export const debtPointSchema = z.object({
  as_of: z.string(),
  outstanding_by_currency: moneyByCurrencySchema,
});

export const supplierDetailSchema = z.object({
  supplier: supplierSchema,
  balance: supplierBalanceSchema,
  invoices: z.array(payableSchema).default([]),
  payments: z.array(paymentSchema).default([]),
  history: z.array(debtPointSchema).default([]),
});

// --- documents ---------------------------------------------------------------

export const DOCUMENT_TYPES = [
  'SUPPLIER_INVOICE',
  'DELIVERY_NOTE',
  'INVENTORY',
  'ACT',
  'OTHER',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_TYPE_TITLES: Record<DocumentType, string> = {
  SUPPLIER_INVOICE: 'Счёт поставщика',
  DELIVERY_NOTE: 'Накладная',
  INVENTORY: 'Инвентаризация',
  ACT: 'Акт',
  OTHER: 'Другое',
};

export const PROCESSING_STATUSES = [
  'NOT_CONFIGURED',
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
] as const;
export type ProcessingStatus = (typeof PROCESSING_STATUSES)[number];

export const PROCESSING_STATUS_TITLES: Record<ProcessingStatus, string> = {
  NOT_CONFIGURED: 'Не настроено',
  PENDING: 'Ожидает',
  RUNNING: 'Выполняется',
  COMPLETED: 'Готово',
  FAILED: 'Ошибка',
};

export const extractionSchema = z.object({
  id: z.string(),
  status: z.enum(['PROPOSED', 'CONFIRMED', 'REJECTED']),
  payload: z.record(z.unknown()),
  /** Which proposed figures could be traced back to the recognised text. */
  numeric_check: z.record(z.unknown()).nullable(),
  created_at: z.string(),
  confirmed_at: z.string().nullable().optional(),
  created_invoice_id: z.string().nullable().optional(),
  created_inventory_document_id: z.string().nullable().optional(),
});

export const operationalDocumentSchema = z.object({
  id: z.string(),
  business_unit_id: z.string(),
  business_unit_name: z.string(),
  supplier_id: z.string().nullable(),
  supplier_name: z.string().nullable(),
  document_type: z.string(),
  document_type_title: z.string(),
  mime_type: z.string(),
  original_filename: z.string(),
  size_bytes: z.number(),
  uploaded_at: z.string(),
  uploaded_by_id: z.string().nullable(),
  ocr_status: z.enum(PROCESSING_STATUSES),
  translation_status: z.enum(PROCESSING_STATUSES),
  ocr_provider: z.string().nullable().optional(),
  ocr_confidence: z.number().nullable().optional(),
  low_confidence: z.boolean().default(false),
  has_ocr_text: z.boolean().default(false),
  has_translation: z.boolean().default(false),
  note: z.string().nullable().optional(),
});

export const operationalDocumentDetailSchema = operationalDocumentSchema.extend({
  /** The Armenian the provider read. Never replaced by the translation. */
  ocr_text_hy: z.string().nullable().optional(),
  /** The Russian, in its own field, always alongside the Armenian. */
  translated_text_ru: z.string().nullable().optional(),
  ocr_error: z.string().nullable().optional(),
  translation_error: z.string().nullable().optional(),
  ocr_processed_at: z.string().nullable().optional(),
  translation_processed_at: z.string().nullable().optional(),
  extractions: z.array(extractionSchema).default([]),
});

export const documentProvidersSchema = z.object({
  ocr_configured: z.boolean(),
  translation_configured: z.boolean(),
  ocr_provider: z.string(),
  translation_provider: z.string(),
});

// --- overview ----------------------------------------------------------------

export const inventoryOverviewSchema = z.object({
  stock_value_by_currency: moneyByCurrencySchema,
  change_by_currency: moneyByCurrencySchema,
  last_inventory_date: z.string().nullable(),
  documents_awaiting_review: z.number(),
  zones_counted: z.number(),
});

/**
 * A module the caller has no grant for arrives as `null`, not as zeros — so
 * the page renders no card at all rather than telling somebody without AP
 * access that the company owes nothing.
 */
export const operationalOverviewSchema = z.object({
  business_unit_id: z.string().nullable(),
  business_unit_name: z.string().nullable(),
  inventory: inventoryOverviewSchema.nullable().optional(),
  accounts_payable: apSummarySchema.nullable().optional(),
  documents_pending: z.number().nullable().optional(),
});

export type ModuleGrant = z.infer<typeof moduleGrantSchema>;
export type UserModuleGrants = z.infer<typeof userModuleGrantsSchema>;
export type OperationalAccess = z.infer<typeof operationalAccessSchema>;
export type InventoryDocument = z.infer<typeof inventoryDocumentSchema>;
export type InventoryDocumentDetail = z.infer<typeof inventoryDocumentDetailSchema>;
export type InventoryLine = z.infer<typeof inventoryLineSchema>;
export type InventoryIssue = z.infer<typeof inventoryIssueSchema>;
export type InventoryComparison = z.infer<typeof inventoryComparisonSchema>;
export type CatalogItem = z.infer<typeof catalogItemSchema>;
export type Supplier = z.infer<typeof supplierSchema>;
export type Payable = z.infer<typeof payableSchema>;
export type Payment = z.infer<typeof paymentSchema>;
export type SupplierBalance = z.infer<typeof supplierBalanceSchema>;
export type SupplierDetail = z.infer<typeof supplierDetailSchema>;
export type ApSummary = z.infer<typeof apSummarySchema>;
export type OperationalDocument = z.infer<typeof operationalDocumentSchema>;
export type OperationalDocumentDetail = z.infer<typeof operationalDocumentDetailSchema>;
export type DocumentProviders = z.infer<typeof documentProvidersSchema>;
export type Extraction = z.infer<typeof extractionSchema>;
export type OperationalOverview = z.infer<typeof operationalOverviewSchema>;
export type InventoryReference = z.infer<typeof referenceSchema>;
